import type { Dapp } from '@src/dto';
import { isRight } from 'fp-ts/Either';
import { useEffect, useState } from 'react';
import type { NavigateFunction } from 'react-router';
import { toast } from 'sonner';
import type { TRPC } from '../../../contexts/TRPC';
import { AuthStateType, type AuthState } from '../../../hooks/useAuth';
import type { DappRouteDapp } from '../../../utils/dappRoutes';
import {
  APP_NFT_REQUIRED_MESSAGE,
  getNewVersionBlockedMessage,
} from '../../../utils/publicationAccess';
import { PUBLISHER_FROZEN_NEW_VERSION_MESSAGE } from '../../../utils/publisherFreeze';
import { logNewVersionDebugPreview } from '../utils/logNewVersionDebugPreview';
import { getDappHomePath } from '../utils/releaseRoutes';

interface UseNewVersionDappDataParams {
  authState: AuthState;
  dappId: string | undefined;
  dappRouteDapp: DappRouteDapp | undefined;
  navigate: NavigateFunction;
  trpc: TRPC['client'];
}

export function useNewVersionDappData({
  authState,
  dappId,
  dappRouteDapp,
  navigate,
  trpc,
}: UseNewVersionDappDataParams) {
  const [dappData, setDappData] = useState<Dapp | null>(null);
  const [dappName, setDappName] = useState('');
  const [isCheckingKyb, setIsCheckingKyb] = useState(true);

  useEffect(() => {
    if (authState.type === AuthStateType.Authenticated) {
      setIsCheckingKyb(false);
    }
  }, [authState]);

  useEffect(() => {
    if (!dappId || isCheckingKyb) {
      return;
    }

    let isCancelled = false;

    void trpc.getDapp.query({ dappId }).then((result) => {
      if (isCancelled) {
        return;
      }

      if (!isRight(result)) {
        toast.error('Failed to load app data', {
          description:
            result.left.message || 'You do not have access to this app.',
          duration: 5000,
        });
        navigate(getDappHomePath(dappRouteDapp));
        return;
      }

      const nextDappData = result.right;
      const newVersionBlockedMessage =
        getNewVersionBlockedMessage(nextDappData);

      if (newVersionBlockedMessage === APP_NFT_REQUIRED_MESSAGE) {
        toast.error('App NFT required to create new version', {
          description:
            'You must mint an App NFT before creating releases. Please mint your App NFT first.',
          duration: 5000,
        });
        navigate(getDappHomePath(dappRouteDapp));
        return;
      }

      if (newVersionBlockedMessage) {
        toast.error(
          newVersionBlockedMessage === PUBLISHER_FROZEN_NEW_VERSION_MESSAGE
            ? 'Publisher account frozen'
            : 'Release publication permission required',
          {
            description: newVersionBlockedMessage,
            duration: 6000,
          },
        );
        navigate(getDappHomePath(dappRouteDapp));
        return;
      }

      if (!nextDappData.dappIconUrl) {
        toast.error('App icon required to create a release', {
          description:
            'Upload an icon for your app before creating a new version.',
          duration: 5000,
        });
        navigate(getDappHomePath(dappRouteDapp));
        return;
      }

      setDappName(nextDappData.dappName);
      setDappData(nextDappData);
      logNewVersionDebugPreview({
        authState,
        dapp: nextDappData,
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [authState, dappId, dappRouteDapp, isCheckingKyb, navigate, trpc]);

  return {
    dappData,
    dappName,
    isCheckingKyb,
  };
}
